
import { useState, useRef, useEffect } from 'react';
import { first30DaysTimeline } from '../../../mocks/first30Days';
import type { TimelineDay } from '../../../mocks/first30Days';
import { Link } from 'react-router-dom';

function TimelineCard({ item, isActive, onClick, index }: { item: TimelineDay; isActive: boolean; onClick: () => void; index: number }) {
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const toggleTask = (taskIndex: number) => {
    setCompletedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskIndex)) {
        next.delete(taskIndex);
      } else {
        next.add(taskIndex);
      }
      return next;
    });
  };

  const progress = item.tasks.length > 0 ? Math.round((completedTasks.size / item.tasks.length) * 100) : 0;

  return (
    <div
      className={`relative transition-all duration-500 ${isActive ? 'opacity-100' : 'opacity-70 hover:opacity-90'}`}
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div
        onClick={onClick}
        className={`relative rounded-xl border-2 transition-all duration-300 cursor-pointer overflow-hidden ${
          isActive
            ? 'border-teal-500 shadow-lg shadow-teal-500/10 bg-white'
            : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-md'
        }`}
      >
        {/* Progress bar at top */}
        <div className="h-1 bg-gray-100 w-full">
          <div
            className="h-full transition-all duration-500 rounded-r-full"
            style={{ width: `${progress}%`, backgroundColor: item.color }}
          />
        </div>

        {/* Header */}
        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <i className={`${item.icon} text-lg`} style={{ color: item.color }}></i>
              </div>
              <div>
                <span
                  className="text-xs font-bold uppercase tracking-wider"
                  style={{ color: item.color }}
                >
                  {item.dayLabel}
                </span>
                <h3 className="text-base font-bold text-gray-900 leading-tight">{item.title}</h3>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0 ml-3">
              <span className="text-xs font-semibold" style={{ color: item.color }}>
                {completedTasks.size}/{item.tasks.length}
              </span>
              <i
                className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${isActive ? 'rotate-180' : ''}`}
                style={{ color: isActive ? item.color : '#9CA3AF' }}
              ></i>
            </div>
          </div>

          <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>
        </div>

        {/* Expandable Content */}
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${
            isActive ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-5 pb-5">
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Tasks to Complete
              </p>
              <ul className="space-y-2.5">
                {item.tasks.map((task, taskIdx) => {
                  const done = completedTasks.has(taskIdx);
                  return (
                    <li key={taskIdx} className="flex items-start gap-3 group">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTask(taskIdx);
                        }}
                        className={`w-5 h-5 flex items-center justify-center rounded-md border-2 shrink-0 mt-0.5 transition-all cursor-pointer ${
                          done
                            ? 'border-transparent'
                            : 'border-gray-300 hover:border-gray-400 bg-white'
                        }`}
                        style={done ? { backgroundColor: item.color, borderColor: item.color } : {}}
                      >
                        {done && <i className="ri-check-line text-white text-xs"></i>}
                      </button>
                      <div className="flex-1 min-w-0">
                        <span
                          className={`text-sm leading-relaxed transition-all ${
                            done ? 'line-through text-gray-400' : 'text-gray-700'
                          }`}
                        >
                          {task.text}
                        </span>
                        {task.link && task.linkLabel && (
                          <Link
                            to={task.link}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-700 ml-1.5 cursor-pointer"
                          >
                            {task.linkLabel}
                            <i className="ri-arrow-right-up-line text-xs"></i>
                          </Link>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Pro Tip */}
            {item.proTip && (
              <div
                className="mt-4 rounded-lg p-3.5 flex items-start gap-2.5"
                style={{ backgroundColor: `${item.color}08`, border: `1px solid ${item.color}20` }}
              >
                <i className="ri-lightbulb-flash-line text-base mt-0.5 shrink-0" style={{ color: item.color }}></i>
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong className="font-semibold" style={{ color: item.color }}>Pro Tip:</strong>{' '}
                  {item.proTip}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function First30DaysSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="first-30-days" className="py-20 px-6 scroll-mt-32" ref={sectionRef}>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-teal-700 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            <i className="ri-calendar-check-line"></i>
            Interactive Timeline
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Your First <span className="text-teal-600">30 Days</span> in Roseville
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
            A week-by-week action plan to help you settle in smoothly. Check off tasks as you go and track your progress.
          </p>
        </div>

        {/* Timeline Navigation Dots */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {first30DaysTimeline.map((item, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className="group flex flex-col items-center gap-1.5 cursor-pointer"
            >
              <div
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-300 ${
                  idx === activeIndex
                    ? 'scale-110 shadow-md'
                    : idx < activeIndex
                    ? 'opacity-60'
                    : 'opacity-40 hover:opacity-70'
                }`}
                style={{
                  backgroundColor: idx === activeIndex ? item.color : `${item.color}20`,
                }}
              >
                <i
                  className={`${item.icon} text-sm`}
                  style={{ color: idx === activeIndex ? '#fff' : item.color }}
                ></i>
              </div>
              <span
                className={`text-[10px] font-semibold whitespace-nowrap transition-all ${
                  idx === activeIndex ? 'opacity-100' : 'opacity-0 group-hover:opacity-60'
                }`}
                style={{ color: item.color }}
              >
                {item.dayLabel}
              </span>
            </button>
          ))}
        </div>

        {/* Timeline Cards */}
        <div className="space-y-4">
          {first30DaysTimeline.map((item, idx) => (
            <div
              key={idx}
              className={`transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <TimelineCard
                item={item}
                isActive={idx === activeIndex}
                onClick={() => setActiveIndex(idx === activeIndex ? idx : idx)}
                index={idx}
              />
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-100 rounded-xl px-6 py-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-100">
              <i className="ri-printer-line text-teal-600 text-lg"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Want a printable version?</p>
              <p className="text-xs text-gray-500">Request your free Welcome Packet with this checklist included.</p>
            </div>
            <a
              href="#contact"
              className="ml-2 bg-teal-600 text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Welcome Packet
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
